import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateReductions } from "state/orderEdition/actions";
import { Form } from "react-bootstrap";
import CurrencyComponent from './CurrencyComponent';

/**
 * Bloc réduction « classique » (réduction unique) — UI d'avant la feature
 * réductions multiples / typées / TVA. Utilisé en prod tant que la nouvelle
 * UI n'est pas activée.
 *
 * L'affichage est identique à l'ancien composant, mais l'écriture se fait
 * dans `order.reductions` (tableau à un élément) — la couche de sauvegarde
 * (multiOrdersStoreToApi) lit désormais ce tableau. Un éventuel `type`/`tva`
 * déjà présent sur la réduction est préservé (une promotion existante garde
 * son type=null et sa TVA à 10 % par défaut côté back).
 */
// Repli à référence stable : renvoyer un littéral depuis le sélecteur créerait un
// nouvel objet à chaque rendu, ce qui re-déclenchait le useEffect ci-dessous à chaque
// action du store et réinitialisait la saisie en cours (bug d'affichage intermittent).
const REDUCTION_VIDE = {};

const ReductionComponentLegacy = ({ orderIndex }) => {
  const dispatch = useDispatch();

  // On sélectionne la commande (référence stable venant du store) et on dérive la
  // réduction courante avec useMemo, plutôt que de construire l'objet dans le sélecteur.
  const order = useSelector((state) => state.orderEdition.orders[orderIndex]);

  // Réduction courante : 1er élément du tableau, repli sur l'ancien champ unique.
  const reduction = useMemo(() => {
    if (!order) {
      return REDUCTION_VIDE;
    }
    if (Array.isArray(order.reductions) && order.reductions.length) {
      return order.reductions[0];
    }
    return order.reduction || REDUCTION_VIDE;
  }, [order]);

  const [reductionType, setReductionType] = useState("");
  const [reductionLabel, setReductionLabel] = useState(reduction.label || null);
  const [directReduction, setDirectReduction] = useState(reduction.direct || null);
  const [proportionalReduction, setProportionalReduction] = useState(reduction.proportional || null);

  useEffect(() => {
    setReductionType(reduction.direct ? "direct" : reduction.proportional ? "proportional" : null);
    setReductionLabel(reduction.label || null);
    setDirectReduction(reduction.direct || null);
    setProportionalReduction(reduction.proportional || null);
  }, [reduction]);

  const handleValueChange = (field, value) => {
    if (field === "direct" || field === "proportional") {
      if (value === "" || value === null || value === undefined || value === 0) {
        setReductionType(null);
      } else {
        setReductionType(field);
      }
    }
    // On conserve `type`/`tva` existants et on écrit dans le tableau `reductions`
    // (1 seul élément). Le filtrage des réductions vides est fait à la sauvegarde.
    const next = { ...reduction, [field]: value };
    dispatch(updateReductions(orderIndex, [next]));
  };

  return (
    <Form>
      <Form.Group>
        <Form.Label>Libellé de la réduction</Form.Label>
        <Form.Control
          id="reductionLabel"
          name="reductionLabel"
          placeholder="Libellé"
          value={reductionLabel === null ? "" : reductionLabel}
          onChange={(e) => {
            setReductionLabel(e.target.value);
            handleValueChange("label", e.target.value);
          }}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>Réduction directe</Form.Label>
        <CurrencyComponent
          initialPrice={directReduction}
          onPriceChange={(newValue) => handleValueChange("direct", newValue)}
          disabled={reductionType === "proportional"}
          style={reductionType === "proportional" ? { backgroundColor: '#cccccc' } : {}}
          nullable={true}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>Réduction proportionnelle (%)</Form.Label>
        <Form.Control
          type="number"
          step="0.01"
          id="proportionalReduction"
          name="proportionalReduction"
          placeholder="Pourcentage"
          value={proportionalReduction || ""}
          onChange={(e) => {
            const newValue = e.target.value;
            setProportionalReduction(newValue);
            handleValueChange("proportional", newValue);
          }}
          disabled={reductionType === "direct"}
          style={reductionType === "direct" ? { backgroundColor: '#cccccc' } : {}}
        />
      </Form.Group>
    </Form>
  );
};

export default ReductionComponentLegacy;
